/**
 * Structured column-filter model for NxTable.
 *
 * Three filter layers combine on a table:
 * 1. **Quick filters** — the classic filter-row inputs (one value list per
 *    column, matched with the column kind's default operator).
 * 2. **Column filters** — operator-based conditions per column, edited in the
 *    header filter popover (`NxTableFilterPopover`).
 * 3. **Advanced filter** — an arbitrarily nested and/or condition tree built
 *    in the filter wizard (`NxTableFilterWizard`).
 *
 * Within a column, quick value + popover conditions combine with the column's
 * own modifier. Across columns, results combine with the table-level
 * `filterModifier`. The advanced tree is always AND-combined at the root.
 *
 * All condition values are carried as strings (numbers via `Number()`, dates
 * as ISO strings, booleans as `'true'`/`'false'`) — the kind drives coercion
 * at evaluation/serialization time.
 */

export type FilterModifier = 'or' | 'and';

/** Value semantics of a column, drives operator set + input widgets + coercion. */
export type NxTableFilterKind =
  | 'string'
  | 'number'
  | 'currency'
  | 'date'
  | 'time'
  | 'select'
  | 'bool';

export type NxTableFilterOperator =
  // string
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEquals'
  | 'startsWith'
  | 'endsWith'
  // number / date / time (equals/notEquals shared)
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  // select
  | 'oneOf'
  | 'noneOf'
  // presence
  | 'empty'
  | 'notEmpty';

export type NxTableFilterCondition = {
  operator: NxTableFilterOperator;
  /** Single comparison value (also the lower bound for `between`). */
  value?: string;
  /** Upper bound for `between` (date "bis", number range end). */
  valueTo?: string;
  /** Value set for `oneOf` / `noneOf`. */
  values?: string[];
};

/** Popover-defined conditions of one column. */
export type NxTableColumnFilter = {
  /** How this column's conditions (incl. its quick value) combine. */
  modifier: FilterModifier;
  conditions: NxTableFilterCondition[];
};

export type NxTableFilterLeaf = {
  columnId: string;
  condition: NxTableFilterCondition;
};

export type NxTableFilterGroup = {
  modifier: FilterModifier;
  children: (NxTableFilterGroup | NxTableFilterLeaf)[];
};

export function isFilterGroup(
  node: NxTableFilterGroup | NxTableFilterLeaf
): node is NxTableFilterGroup {
  return 'children' in node;
}

/** A named, re-loadable snapshot of the complete filter state of a table. */
export type NxSavedTableFilter = {
  id: string;
  name: string;
  /**
   * Place among the table's filter sets: sets with an order come first,
   * lowest first; the others follow by name. A consumer that shows fixed sets
   * of its own (predefined views) orders them; a set the user saves has none.
   */
  order?: number;
  filterModifier: FilterModifier;
  /** Filter-row values per column (legacy quick filters). */
  quickFilters: Record<string, string[]>;
  /** Popover conditions per column. */
  columnFilters: Record<string, NxTableColumnFilter>;
  advancedFilter: NxTableFilterGroup | null;
};

/** Column metadata the filter UIs (popover, wizard, chips) work with. */
export type NxTableFilterColumnMeta = {
  columnId: string;
  displayName: string;
  kind: NxTableFilterKind;
  options?: string[];
  /** Display label for a select option value (e.g. enum code → localized label). */
  optionLabel?: (value: string) => string;
  /** Show the search box in the header-filter checklist (default true). */
  searchable?: boolean;
  /** Operators the filter UIs offer for the column (default: all of its kind); see `filterOperators`. */
  operators?: readonly NxTableFilterOperator[];
  /** How several conditions of the column may combine (default: and + or); see `filterModifiers`. */
  modifiers?: readonly FilterModifier[];
};

export const FILTER_OPERATORS_BY_KIND: Record<NxTableFilterKind, NxTableFilterOperator[]> = {
  string: [
    'contains',
    'notContains',
    'equals',
    'notEquals',
    'startsWith',
    'endsWith',
    'empty',
    'notEmpty',
  ],
  number: ['equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'notEmpty'],
  currency: ['equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'notEmpty'],
  date: ['between', 'equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'empty', 'notEmpty'],
  time: ['between', 'equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'empty', 'notEmpty'],
  select: ['oneOf', 'noneOf', 'empty', 'notEmpty'],
  bool: ['equals', 'notEquals'],
};

/** The kinds whose funnel is the one-click value checklist (which speaks `oneOf` / `noneOf`). */
const CHECKLIST_KINDS: readonly NxTableFilterKind[] = ['string', 'select', 'bool'];
const CHECKLIST_OPERATORS: readonly NxTableFilterOperator[] = ['oneOf', 'noneOf'];

/**
 * What a column's `filterOperators` really restricts to — ONE rule for every helper
 * below. `undefined` = unrestricted: no list, an empty list, a list that names
 * nothing the funnel supports for the kind, or one that leaves it neither the
 * checklist (`oneOf`) nor an editor operator (`['noneOf']` on a text column).
 * Otherwise the listed operators the
 * funnel supports for the kind: its editor operators (`FILTER_OPERATORS_BY_KIND`) plus,
 * for a text, select or bool column, the checklist operators `oneOf` / `noneOf`.
 */
export function supportedFilterOperators(
  kind: NxTableFilterKind,
  allowed?: readonly NxTableFilterOperator[]
): NxTableFilterOperator[] | undefined {
  if (!allowed || allowed.length === 0) return undefined;
  const supported = [
    ...FILTER_OPERATORS_BY_KIND[kind],
    ...(CHECKLIST_KINDS.includes(kind) ? CHECKLIST_OPERATORS : []),
  ];
  const kept = allowed.filter((operator) => supported.includes(operator));
  // A list that leaves the funnel neither the checklist (`oneOf`) nor an editor operator
  // (e.g. `['noneOf']` on a text column) would be empty: unrestricted, like `[]`.
  const usable = kept.some(
    (operator) => operator === 'oneOf' || FILTER_OPERATORS_BY_KIND[kind].includes(operator)
  );
  return usable ? kept : undefined;
}

/**
 * The operators the condition editor (header funnel "advanced", filter wizard) offers
 * for a column: those of `FILTER_OPERATORS_BY_KIND` that `supportedFilterOperators`
 * keeps, in the kind's order — all of them when unrestricted. May be EMPTY: a text
 * column restricted to `['oneOf']` is the checklist only and has no editor operators.
 */
export function restrictedFilterOperators(
  kind: NxTableFilterKind,
  allowed?: readonly NxTableFilterOperator[]
): NxTableFilterOperator[] {
  const kept = supportedFilterOperators(kind, allowed);
  const all = FILTER_OPERATORS_BY_KIND[kind];
  return kept ? all.filter((operator) => kept.includes(operator)) : all;
}

/** Whether the column offers `operator` (editor or checklist): always when unrestricted. */
export function allowsFilterOperator(
  kind: NxTableFilterKind,
  allowed: readonly NxTableFilterOperator[] | undefined,
  operator: NxTableFilterOperator
): boolean {
  const kept = supportedFilterOperators(kind, allowed);
  return !kept || kept.includes(operator);
}

/** Operator applied to quick filter-row values of a column. */
export function defaultFilterOperator(kind: NxTableFilterKind): NxTableFilterOperator {
  switch (kind) {
    case 'string':
      return 'contains';
    case 'select':
      return 'oneOf';
    default:
      return 'equals';
  }
}

export type FilterOperatorArity = 'none' | 'single' | 'range' | 'multi';

export function filterOperatorArity(operator: NxTableFilterOperator): FilterOperatorArity {
  switch (operator) {
    case 'empty':
    case 'notEmpty':
      return 'none';
    case 'between':
      return 'range';
    case 'oneOf':
    case 'noneOf':
      return 'multi';
    default:
      return 'single';
  }
}

const hasText = (v: string | undefined): v is string => v !== undefined && v !== '';

/** A condition that carries every value its operator needs. */
export function isConditionComplete(condition: NxTableFilterCondition): boolean {
  switch (filterOperatorArity(condition.operator)) {
    case 'none':
      return true;
    case 'single':
      return hasText(condition.value);
    case 'range':
      // Open ranges are allowed: one bound acts as ≥ / ≤.
      return hasText(condition.value) || hasText(condition.valueTo);
    case 'multi':
      return (condition.values?.length ?? 0) > 0;
  }
}

/** Drop incomplete conditions; `undefined` when nothing effective remains. */
export function normalizeColumnFilter(
  filter: NxTableColumnFilter | undefined | null
): NxTableColumnFilter | undefined {
  if (!filter) return undefined;
  const conditions = filter.conditions.filter(isConditionComplete);
  if (conditions.length === 0) return undefined;
  return { modifier: filter.modifier, conditions };
}

/** Drop incomplete leaves and empty groups; `null` when nothing remains. */
export function normalizeFilterGroup(
  group: NxTableFilterGroup | undefined | null
): NxTableFilterGroup | null {
  if (!group) return null;
  const children = group.children
    .map((child) =>
      isFilterGroup(child)
        ? normalizeFilterGroup(child)
        : isConditionComplete(child.condition)
          ? child
          : null
    )
    .filter((child): child is NxTableFilterGroup | NxTableFilterLeaf => child !== null);
  if (children.length === 0) return null;
  return { modifier: group.modifier, children };
}

/** Number of complete conditions in a tree (for badges/summaries). */
export function countGroupConditions(group: NxTableFilterGroup | null | undefined): number {
  if (!group) return 0;
  return group.children.reduce(
    (sum, child) =>
      sum +
      (isFilterGroup(child)
        ? countGroupConditions(child)
        : isConditionComplete(child.condition)
          ? 1
          : 0),
    0
  );
}

export function cloneFilterGroup(group: NxTableFilterGroup): NxTableFilterGroup {
  return {
    modifier: group.modifier,
    children: group.children.map((child) =>
      isFilterGroup(child)
        ? cloneFilterGroup(child)
        : { columnId: child.columnId, condition: cloneCondition(child.condition) }
    ),
  };
}

export function cloneCondition(condition: NxTableFilterCondition): NxTableFilterCondition {
  return {
    operator: condition.operator,
    ...(condition.value !== undefined ? { value: condition.value } : {}),
    ...(condition.valueTo !== undefined ? { valueTo: condition.valueTo } : {}),
    ...(condition.values !== undefined ? { values: [...condition.values] } : {}),
  };
}

export function cloneColumnFilter(filter: NxTableColumnFilter): NxTableColumnFilter {
  return { modifier: filter.modifier, conditions: filter.conditions.map(cloneCondition) };
}

// ---- Evaluation (client-side data) ----

/**
 * Ordering coercion per kind. Returns `null` for absent/unparseable values —
 * ordered comparisons on `null` are always false (only empty/notEmpty match).
 */
function toComparable(kind: NxTableFilterKind, raw: unknown): number | string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  switch (kind) {
    case 'number':
    case 'currency': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'date': {
      const t = raw instanceof Date ? raw.getTime() : Date.parse(String(raw));
      return Number.isFinite(t) ? t : null;
    }
    case 'time':
      // 'HH:MM[:SS]' compares lexicographically.
      return String(raw);
    default:
      return String(raw);
  }
}

function conditionSideToComparable(
  kind: NxTableFilterKind,
  value: string | undefined
): number | string | null {
  return toComparable(kind, value);
}

const isEmptyValue = (raw: unknown): boolean =>
  raw === null || raw === undefined || String(raw) === '';

function normalizedString(raw: unknown): string {
  return String(raw ?? '').toLocaleLowerCase();
}

function boolString(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw === 'true' || raw === '1' ? 'true' : raw === '' ? '' : 'false';
  }
  if (raw === null || raw === undefined) return '';
  return raw ? 'true' : 'false';
}

/** Evaluate one condition against one raw cell value. */
export function matchesCondition(
  kind: NxTableFilterKind,
  raw: unknown,
  condition: NxTableFilterCondition
): boolean {
  const { operator } = condition;
  if (operator === 'empty') return isEmptyValue(raw);
  if (operator === 'notEmpty') return !isEmptyValue(raw);

  if (operator === 'oneOf' || operator === 'noneOf') {
    const values = condition.values ?? [];
    const rawStr = kind === 'bool' ? boolString(raw) : String(raw ?? '');
    const included = values.includes(rawStr);
    return operator === 'oneOf' ? included : !included;
  }

  if (kind === 'bool') {
    const rawStr = boolString(raw);
    if (rawStr === '') return false;
    const want = condition.value === 'true' ? 'true' : 'false';
    return operator === 'notEquals' ? rawStr !== want : rawStr === want;
  }

  if (kind === 'string' || kind === 'select') {
    const value = String(raw ?? '');
    const needle = condition.value ?? '';
    switch (operator) {
      case 'equals':
        // "exakt wie": exact, case-sensitive match.
        return !isEmptyValue(raw) && value === needle;
      case 'notEquals':
        return value !== needle;
      case 'contains':
        return !isEmptyValue(raw) && normalizedString(value).includes(normalizedString(needle));
      case 'notContains':
        return !normalizedString(value).includes(normalizedString(needle));
      case 'startsWith':
        return !isEmptyValue(raw) && normalizedString(value).startsWith(normalizedString(needle));
      case 'endsWith':
        return !isEmptyValue(raw) && normalizedString(value).endsWith(normalizedString(needle));
      default:
        return false;
    }
  }

  // Ordered kinds: number / currency / date / time.
  const rowCmp = toComparable(kind, raw);
  if (rowCmp === null) return operator === 'notEquals';
  switch (operator) {
    case 'equals': {
      const want = conditionSideToComparable(kind, condition.value);
      return want !== null && rowCmp === want;
    }
    case 'notEquals': {
      const want = conditionSideToComparable(kind, condition.value);
      return want === null || rowCmp !== want;
    }
    case 'gt': {
      const want = conditionSideToComparable(kind, condition.value);
      return want !== null && rowCmp > want;
    }
    case 'gte': {
      const want = conditionSideToComparable(kind, condition.value);
      return want !== null && rowCmp >= want;
    }
    case 'lt': {
      const want = conditionSideToComparable(kind, condition.value);
      return want !== null && rowCmp < want;
    }
    case 'lte': {
      const want = conditionSideToComparable(kind, condition.value);
      return want !== null && rowCmp <= want;
    }
    case 'between': {
      const lo = conditionSideToComparable(kind, condition.value);
      const hi = conditionSideToComparable(kind, condition.valueTo);
      if (lo !== null && rowCmp < lo) return false;
      if (hi !== null && rowCmp > hi) return false;
      return lo !== null || hi !== null;
    }
    default:
      return false;
  }
}

const combine = (modifier: FilterModifier, results: boolean[]): boolean =>
  results.length === 0 ? true : modifier === 'and' ? results.every(Boolean) : results.some(Boolean);

export type NxTableFilterEvalOptions<T> = {
  /** Filter-row values per column (matched with the kind's default operator). */
  quickFilters?: ReadonlyMap<string, readonly unknown[]>;
  /** Popover conditions per column. */
  columnFilters?: ReadonlyMap<string, NxTableColumnFilter>;
  /** Wizard tree — AND-combined with the column layers. */
  advancedFilter?: NxTableFilterGroup | null;
  /** Cross-column combination of the per-column results. Defaults to 'and'. */
  filterModifier?: FilterModifier;
  /** Column kind lookup; unknown columns evaluate as 'string'. */
  kinds?: ReadonlyMap<string, NxTableFilterKind>;
  /** Cell accessor; defaults to `row[columnId]`. */
  getValue?: (row: T, columnId: string) => unknown;
};

/** Quick filter-row values → one condition with the kind's default operator. */
export function quickValuesToCondition(
  kind: NxTableFilterKind,
  values: readonly unknown[]
): NxTableFilterCondition | null {
  const strings = values.filter((v) => v !== null && v !== undefined && v !== '').map(String);
  if (strings.length === 0) return null;
  const operator = defaultFilterOperator(kind);
  if (filterOperatorArity(operator) === 'multi') {
    return { operator, values: strings };
  }
  return { operator, value: strings[0] };
}

/** Evaluate all filter layers against one row. */
export function rowMatchesFilters<T>(row: T, options: NxTableFilterEvalOptions<T>): boolean {
  const kindOf = (columnId: string): NxTableFilterKind => options.kinds?.get(columnId) ?? 'string';
  const getValue =
    options.getValue ?? ((r: T, columnId: string) => (r as Record<string, unknown>)[columnId]);

  const columnIds = new Set<string>([
    ...(options.quickFilters?.keys() ?? []),
    ...(options.columnFilters?.keys() ?? []),
  ]);

  const columnResults: boolean[] = [];
  for (const columnId of columnIds) {
    const kind = kindOf(columnId);
    const raw = getValue(row, columnId);
    const conditions: NxTableFilterCondition[] = [];
    const quick = options.quickFilters?.get(columnId);
    if (quick) {
      const quickCondition = quickValuesToCondition(kind, quick);
      if (quickCondition) conditions.push(quickCondition);
    }
    const columnFilter = normalizeColumnFilter(options.columnFilters?.get(columnId));
    if (columnFilter) conditions.push(...columnFilter.conditions);
    if (conditions.length === 0) continue;
    const modifier = columnFilter?.modifier ?? 'and';
    columnResults.push(
      combine(
        modifier,
        conditions.map((condition) => matchesCondition(kind, raw, condition))
      )
    );
  }

  const columnsMatch = combine(options.filterModifier ?? 'and', columnResults);
  if (!columnsMatch) return false;

  const advanced = normalizeFilterGroup(options.advancedFilter);
  if (!advanced) return true;
  return evaluateGroup(row, advanced, kindOf, getValue);
}

function evaluateGroup<T>(
  row: T,
  group: NxTableFilterGroup,
  kindOf: (columnId: string) => NxTableFilterKind,
  getValue: (row: T, columnId: string) => unknown
): boolean {
  return combine(
    group.modifier,
    group.children.map((child) =>
      isFilterGroup(child)
        ? evaluateGroup(row, child, kindOf, getValue)
        : matchesCondition(kindOf(child.columnId), getValue(row, child.columnId), child.condition)
    )
  );
}

/** Convenience: filter a client-side row array through every layer. */
export function applyTableFilters<T>(
  rows: readonly T[],
  options: NxTableFilterEvalOptions<T>
): T[] {
  return rows.filter((row) => rowMatchesFilters(row, options));
}
